'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { swapTeamsInBracket } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import type { KnockoutNode, Team } from '@/types/tournament.model';
import { getTeamName } from '@/utils/team-utils';
import {
  getBracketEntries,
  getTeamsPlayedInBracket,
  type BracketEntry,
} from '@/utils/fixture-utils';
import { knockoutRoundNames } from './knockout/useKnockoutStageData';
import { SwapStepsFooter } from './SwapStepsFooter';
import { SwapTeamSelectButton, SwapTeamSummaryBlock } from './SwapTeamOption';

type Spot = BracketEntry & { label: string };

type TeamSelection = {
  team: Team;
  spot: Spot;
};

interface SwapTeamsInBracketModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  /** Tournament sport UID (used to pick the correct ranking for ELO). */
  tournamentSportUid?: string;
  rootNode: KnockoutNode;
  /** With an active fixture players already see the bracket, so they get notified. */
  isFixtureActive: boolean;
  onSuccess: () => void;
}

export function SwapTeamsInBracketModal({
  open,
  onOpenChange,
  tournamentId,
  tournamentSportUid,
  rootNode,
  isFixtureActive,
  onSuccess,
}: SwapTeamsInBracketModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [teamA, setTeamA] = useState<TeamSelection | null>(null);
  const [teamB, setTeamB] = useState<TeamSelection | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setTeamA(null);
      setTeamB(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (!teamA || !teamB) return;
    try {
      setIsSubmitting(true);
      await swapTeamsInBracket(tournamentId, teamA.team.id, teamB.team.id);
      toast({
        title: 'Intercambio realizado',
        description: 'El cuadro se actualizó correctamente.',
        variant: 'default',
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      logError('SwapTeamsInBracketModal.handleConfirm', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo intercambiar los equipos. Inténtalo de nuevo.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { spots, playedTeamIds, canSwap } = useMemo(() => {
    const roundNames = knockoutRoundNames(rootNode.round);
    let matchNumber = 0;
    const spots: Spot[] = getBracketEntries(rootNode).map((entry) => ({
      ...entry,
      label: entry.isBye
        ? `Pasa directo a ${roundNames[2]}`
        : `${roundNames[1]} · Cruce ${++matchNumber}`,
    }));
    const playedTeamIds = getTeamsPlayedInBracket(rootNode);
    // Two teams that haven't played are not enough if they already meet in their first match.
    const swappableMatchups = new Set(
      spots
        .filter((spot) => spot.teams.some((t) => !playedTeamIds.has(t.id)))
        .map((spot) => spot.matchupKey),
    );
    return { spots, playedTeamIds, canSwap: swappableMatchups.size >= 2 };
  }, [rootNode]);

  const [selected, select] =
    step === 1 ? ([teamA, setTeamA] as const) : ([teamB, setTeamB] as const);

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Intercambiar equipos
          </DialogTitle>
          <DialogDescription>
            Elegí dos equipos de cruces distintos: cada uno pasa a jugar el
            cruce del otro, con su día, hora y cancha. Si uno pasa directo, el
            otro se queda con ese pase.
            {isFixtureActive &&
              ' Con el fixture activo solo se pueden mover los equipos que todavía no jugaron, y a los jugadores de los dos equipos les llega un aviso.'}
          </DialogDescription>
        </DialogHeader>

        {!canSwap ? (
          <p className="py-4 text-sm text-muted-foreground">
            No quedan dos equipos de cruces distintos que todavía no hayan
            jugado, así que no hay nada para intercambiar.
          </p>
        ) : (
          <div className="max-h-[min(420px,55vh)] space-y-4 overflow-y-auto pr-1">
            {step !== 3 && (
              <div className="space-y-3">
                {step === 2 && teamA && (
                  <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">
                      Seleccionado:{' '}
                    </span>
                    <span className="font-medium">
                      {getTeamName(teamA.team)}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      ({teamA.spot.label})
                    </span>
                  </div>
                )}
                <p className="text-sm font-medium text-foreground">
                  {step === 1
                    ? '1. Elegí el primer equipo'
                    : '2. Elegí el otro equipo'}
                </p>
                {spots.map((spot, spotIndex) =>
                  // Its rival in the first match would only trade sides with it.
                  step === 2 &&
                  spot.matchupKey === teamA?.spot.matchupKey ? null : (
                    <div
                      key={spotIndex}
                      className="rounded-lg border bg-muted/30 p-3"
                    >
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {spot.label}
                      </p>
                      <ul className="space-y-2">
                        {spot.teams.map((team) => (
                          <li key={team.id}>
                            <SwapTeamSelectButton
                              team={team}
                              selected={selected?.team.id === team.id}
                              tournamentSportUid={tournamentSportUid}
                              hasPlayed={playedTeamIds.has(team.id)}
                              onSelect={() => select({ team, spot })}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )}
              </div>
            )}

            {step === 3 && teamA && teamB && (
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-sm font-medium">Confirmar intercambio</p>
                <p className="text-sm text-muted-foreground">
                  Se intercambiarán de lugar en el cuadro:
                </p>
                <div className="space-y-3">
                  <SwapTeamSummaryBlock
                    team={teamA.team}
                    label={teamA.spot.label}
                    tournamentSportUid={tournamentSportUid}
                  />
                  <div className="flex justify-center text-muted-foreground">
                    <ArrowLeftRight className="h-4 w-4" />
                  </div>
                  <SwapTeamSummaryBlock
                    team={teamB.team}
                    label={teamB.spot.label}
                    tournamentSportUid={tournamentSportUid}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <SwapStepsFooter
          step={step}
          canSwap={canSwap}
          isSubmitting={isSubmitting}
          nextDisabled={step === 1 ? !teamA : !teamB}
          onCancel={handleClose}
          onBack={() => {
            if (step === 3) return setStep(2);
            setTeamB(null);
            setStep(1);
          }}
          onNext={() => setStep(step === 1 ? 2 : 3)}
          onConfirm={handleConfirm}
        />
      </DialogContent>
    </Dialog>
  );
}
